/**
 * Teardown for a session that nothing is left to discharge a step-up over.
 *
 * Shared by the OAuth callback and the TOTP step-up page so the two exits
 * cannot drift. Both hold an aal1 session the credential exchange has ALREADY
 * persisted, and web AAL enforcement is client-side (ADR 0068), so a session
 * left in storage is a session the next visitor reaches the app on.
 */

import { supabase } from '@/integrations/supabase/client';
import { clearAllAppData } from '@/lib/mobile/auth-persistence';

/**
 * How long the server-side revoke gets before the fallbacks run without it.
 *
 * Same budget as the password step-up's dismissal
 * (`use-password-sign-in.ts`), so a session abandoned at the OAuth landing
 * costs the same wait as one abandoned at the password challenge.
 */
export const SIGN_OUT_REVOKE_GRACE_MS = 2000;

/**
 * Last resort: destroy the PERSISTED copy of the session outright.
 *
 * `clearAllAppData()` is the documented single owner of auth-storage teardown.
 * The store of record differs per shell - `localStorage` on web, the bridge's
 * `authStorage` on desktop, the Keychain on native - so removing one key by
 * hand is indistinguishable from working on the shells that use the others.
 *
 * WHAT IT CLEARS: the persisted session, and nothing else. auth-js reads the
 * session back off storage, so every later auth call resolves signed-out and no
 * reload revives it.
 *
 * WHAT IT DOES NOT CLEAR, knowingly: no SIGNED_OUT is emitted on this path -
 * the scopes that would have emitted one have already failed or hung - so
 * AuthContext's in-memory user/session and the TanStack cache, org membership
 * included, survive in this tab until a reload. React state is not storage.
 * Clearing it would take the context's own logout (`auth-context.tsx`
 * `signOut`), which lib code cannot reach, and synthesising a SIGNED_OUT has no
 * public API. The password and passkey dismissal fallbacks
 * (`use-password-sign-in.ts` `dismissMFAAndSignOut`, `use-passkey-sign-in.ts`
 * `clearLocalSession`) carry the same residual for the same reason, and both
 * callers of this helper navigate to a sign-in surface as they fire it - so it
 * is accepted as the established last-resort shape rather than half-fixed with
 * a bespoke bus into the context.
 *
 * WHAT IT IS NOT: a fix for the route guard. `protected-route.tsx` reads the
 * LOSSY `checkMFARequirement()`, which answers `needsMFA: false` on every error
 * path, so the storage-null state this purge leaves behind resolves there as
 * "nothing owed" and the outlet is admitted on the in-memory user above. That
 * guard is availability-over-certainty BY DECISION - ADR 0068's recorded
 * residual risk 1 - and moving it onto the strict check is a policy change this
 * helper neither makes nor compensates for.
 */
async function purgePersistedSession(label: string): Promise<void> {
  try {
    await clearAllAppData();
  } catch (error) {
    console.error(
      `${label} auth-storage purge threw:`,
      error instanceof Error ? error.name : 'unknown',
    );
  }
}

/**
 * Drop the client's own copy of the session.
 *
 * NOT a storage-only operation, despite the scope name. Version note first,
 * because the pnpm store misleads here: `frontend/web/package.json` declares
 * `@supabase/supabase-js` 2.105.3, and that instance resolves its own
 * `@supabase/auth-js` 2.105.3 sibling - the auth-js 2.99.3 also present in
 * the store belongs to the `backend/workers/*` workspace packages (they pin
 * supabase-js 2.99.3), so reading a version off the store names the wrong
 * instance for this frontend. The behaviour below is byte-identical in
 * auth-js 2.99.3 and 2.105.3 (verified in each version's dist
 * `GoTrueClient.js`): `_signOut` calls `admin.signOut(accessToken, scope)`
 * for the `local` scope too. `admin.signOut` reports a failed round trip on
 * the RESOLVED channel, and a network failure arrives there as
 * `AuthRetryableFetchError` - which the ignore-list guard (`isAuthApiError`,
 * i.e. `name === 'AuthApiError'`) does not match, so `_signOut` returns
 * BEFORE its own `_removeSession()`. On exactly the failure this call exists
 * to answer, nothing is removed. Hence both channels are read and both report
 * failure, which the caller answers with the purge.
 */
async function clearLocalSession(label: string): Promise<boolean> {
  try {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error === null) {
      return true;
    }
    console.error(`${label} local sign-out failed:`, error.code);
  } catch (error) {
    console.error(
      `${label} local sign-out threw:`,
      error instanceof Error ? error.name : 'unknown',
    );
  }
  return false;
}

/**
 * The global revoke, reporting only whether the session is actually gone.
 *
 * Split out so the caller can hold it as a promise: a revoke that neither
 * resolves nor rejects has to be raced rather than awaited, and the outcome is
 * still needed once it lands.
 */
async function revokeServerSide(label: string): Promise<boolean> {
  try {
    // Scope local: the abandoned session is the one to revoke; global would
    // take the user's other devices with it.
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error === null) {
      return true;
    }
    console.error(`${label} sign-out failed:`, error.code);
  } catch (error) {
    console.error(`${label} sign-out threw:`, error instanceof Error ? error.name : 'unknown');
  }
  return false;
}

/** Race marker for a revoke still in flight when the grace runs out. */
const GRACE_EXPIRED = 'grace-expired';

/**
 * Revoke a session whose step-up obligation nothing is left to discharge.
 *
 * Global scope first with the local scope as the fallback - the same shape the
 * password and passkey step-up dismissals use (`use-password-sign-in.ts`,
 * `use-passkey-sign-in.ts`), so an abandoned step-up costs the same set of
 * sessions whichever credential opened it.
 *
 * `signOut()` reports a failed revoke on the RESOLVED channel and rejects only
 * when the call throws, so a `try`/`catch` alone reads a server-side failure as
 * success. Name and code ONLY in the log: an `AuthError` keeps the originating
 * failure on `cause`, and Sentry's linkedErrors integration walks cause chains
 * into telemetry.
 *
 * The revoke STARTS before either fallback and is only raced, never awaited
 * without a bound. It must start first because auth-js reads the persisted
 * session to obtain the token it revokes server-side, so a purge that ran ahead
 * of it would silently skip revocation. It cannot be awaited outright because
 * browser fetch has no default timeout: an offline or black-holed request
 * neither resolves nor rejects, and every step below it - including the purge
 * that exists for exactly this case - would wait on it forever.
 */
export async function revokeAbandonedSession(label: string): Promise<void> {
  const revocation = revokeServerSide(label);
  let graceTimer: ReturnType<typeof setTimeout> | undefined;
  const outcome = await Promise.race([
    revocation,
    new Promise<typeof GRACE_EXPIRED>((resolve) => {
      graceTimer = setTimeout(() => {
        resolve(GRACE_EXPIRED);
      }, SIGN_OUT_REVOKE_GRACE_MS);
    }),
  ]);
  clearTimeout(graceTimer);

  if (outcome === true) {
    return;
  }

  if (outcome === GRACE_EXPIRED) {
    // Still in flight, so auth-js still holds its lock: the local scope queues
    // behind that same lock and would inherit the same unbounded wait. It is
    // fired anyway but NOT awaited - it drops the in-memory token whenever the
    // lock frees - while the purge, the only step that cannot be blocked by
    // auth-js, runs now. Errors are handled inside, so the floating promise
    // never rejects unhandled.
    void clearLocalSession(label);
    await purgePersistedSession(label);
    return;
  }

  if (await clearLocalSession(label)) {
    return;
  }
  await purgePersistedSession(label);
}
